import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQualityControlTool } from '@/context/Qualitycontrol.context';
import { ErrorField, ErrorType } from '@/types/QcInfo';
import AdditionalInfoSection from './AdditionalInfoSection';

interface RefurbishmentInfoProps {
  isViewMode: boolean;
  showWatchFunctioned: boolean;
  errors?: ErrorType[];
}

export default function RemarkInfo({
  isViewMode,
  showWatchFunctioned,
  errors,
}: RefurbishmentInfoProps) {
  const { t } = useTranslation();

  const { additionalInfo, setAdditionalInfo, isForceSellerReturnEditable } =
    useQualityControlTool();

  const { isWorkingWatch } = additionalInfo || {};

  const watchValue = useMemo(() => {
    if (isWorkingWatch == null) return '';
    return isWorkingWatch ? 'true' : 'false';
  }, [isWorkingWatch]);

  const watchError = useMemo(() => {
    if (isWorkingWatch != null) return undefined;
    return errors && errors.find((e) => e.field === ErrorField.WATCH);
  }, [errors, isWorkingWatch]);

  const renderContent = () => {
    return (
      <Stack mb={0.5} mt="-8px">
        <FormControlLabel
          sx={{
            display: 'flex',
          }}
          control={
            <Checkbox
              disabled={isViewMode}
              data-testid="refurbishment_checkbox"
              checked={additionalInfo.refurbishment}
              onChange={(e) => {
                setAdditionalInfo((prev) => ({
                  ...prev,
                  refurbishment: e.target.checked,
                }));
              }}
            />
          }
          label={
            <Typography variant="body2">
              {t('QC_V2.MAIN.REFURBISHMENT_DONE')}
            </Typography>
          }
        />

        <FormControlLabel
          sx={{
            display: 'flex',
          }}
          control={
            <Checkbox
              disabled={isViewMode || !isForceSellerReturnEditable}
              data-testid="force_return_checkbox"
              checked={additionalInfo.forceSellerReturn}
              onChange={(e) => {
                setAdditionalInfo((prev) => ({
                  ...prev,
                  forceSellerReturn: e.target.checked,
                }));
              }}
            />
          }
          label={
            <Typography variant="body2">
              {t('QC_V2.MAIN.NEGO_KO_FORCE_RETURN')}
            </Typography>
          }
        />

        {!isViewMode && !isForceSellerReturnEditable && (
          <FormHelperText
            data-testid="force_return_locked_message"
            sx={{ ml: 4, color: (theme) => theme.palette.primary.main }}
          >
            {t(
              additionalInfo.forceSellerReturn
                ? 'QC_V2.MAIN.FORCE_RETURN_LOCKED_BY_SELLER_FLAG'
                : 'QC_V2.MAIN.FORCE_RETURN_LOCKED_OUTSIDE_CUSTOMS_ZONE'
            )}
          </FormHelperText>
        )}

        {showWatchFunctioned && (
          <FormControl
            error={!!watchError}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <FormLabel
              sx={{
                mr: 2,
                '&.Mui-focused': {
                  color: (theme) => theme.palette.common.black,
                },
              }}
            >
              {t('QC_V2.MAIN.DOES_WATCH_FUNCTION')}
            </FormLabel>

            <RadioGroup
              row
              name="controlled-radio-buttons-group"
              value={watchValue}
              onChange={(e) =>
                setAdditionalInfo((prev) => ({
                  ...prev,
                  isWorkingWatch: e.target.value === 'true',
                }))
              }
            >
              <FormControlLabel
                value="true"
                disabled={isViewMode}
                control={<Radio />}
                label={t('QC_V2.MAIN.YES')}
              />
              <FormControlLabel
                value="false"
                disabled={isViewMode}
                control={<Radio />}
                label={t('QC_V2.MAIN.NO')}
              />
            </RadioGroup>

            {!!watchError?.message && (
              <FormHelperText sx={{ mx: 1 }}>
                {watchError.message}
              </FormHelperText>
            )}
          </FormControl>
        )}
      </Stack>
    );
  };

  return (
    <AdditionalInfoSection
      title={t('QC_V2.MAIN.REMARK')}
      content={renderContent()}
    />
  );
}
